import { useEffect, useRef, useState } from 'react';
import { useRoutes } from 'react-router-dom';

import { Login } from '@/features/auth/routes/Login';
import { useAuth } from '@/lib/auth';
import { lazyImport } from '@/utils/lazyImport';

import { protectedRoutes } from './protected';
import { publicRoutes } from './public';
import { usePharma } from '@/providers/PharmaProvider';
import { useCapabilities } from '@/providers/useCapabilities';

export const AppRoutes = () => {
  const auth = useAuth();
  const pharma = usePharma();
  const { isDfiCrc, isDfiStudyTeam, isDfiVv, isDfiDeviceAdmin } = useCapabilities();

  // DFI18231 Site Staff backstop: a correctly-resolved staff session runs AS its
  // Site and never exposes this raw flag. If auth.user reports it, a raw staff
  // session slipped the server guards -- log it out and never render protected
  // routes. (No early return: useRoutes below must run on every render.)
  const isRawSiteStaff = !!auth.user?.flags?.includes('DFI18231_SITE_STAFF');
  const { logout } = auth;
  const { pharmaLogout } = pharma;
  const logoutStarted = useRef(false);
  const [logoutFailed, setLogoutFailed] = useState(false);
  const [logoutAttempt, setLogoutAttempt] = useState(0);
  useEffect(() => {
    if (!isRawSiteStaff) {
      logoutStarted.current = false;
      setLogoutFailed(false);
      return;
    }
    // Provider resets and auth mutations both rerender. Start only one request
    // for this invalid session, even if a callback changes while it is pending.
    if (logoutStarted.current) return;
    logoutStarted.current = true;
    setLogoutFailed(false);
    pharmaLogout();
    Promise.resolve()
      .then(() => logout())
      .catch(() => setLogoutFailed(true));
  }, [isRawSiteStaff, logout, pharmaLogout, logoutAttempt]);

  const invalidSession = (
    <div role="alert">
      {logoutFailed ? (
        <>
          <p>Could not sign out. Please try again.</p>
          <button
            onClick={() => {
              logoutStarted.current = false;
              setLogoutAttempt((attempt) => attempt + 1);
            }}
          >
            Try again
          </button>
        </>
      ) : (
        <p>Signing out…</p>
      )}
    </div>
  );

  // const commonRoutes = [{ path: '/', element: <Landing /> }];
  const commonRoutes = [{ path: '/', element: isRawSiteStaff ? invalidSession : <Login /> }];

  if (!auth.user) {
    commonRoutes.push({ path: '*', element: <Login /> });
  }

  if (parseInt(auth.user?.is_mod as string) || parseInt(auth.user?.is_admin as string)) {
    const { ModToolsRoutes } = lazyImport(() => import('@/features/mod_tools'), 'ModToolsRoutes');
    protectedRoutes &&
      protectedRoutes[0].children &&
      protectedRoutes[0].children.push({
        path: '/mod_tools/*',
        element: <ModToolsRoutes />,
      });
  }

  let routes = auth.user ? protectedRoutes : publicRoutes;

  // if ((pharma.isCRC || pharma.isPV) && !auth.user) {
  //   routes = publicRoutes;
  // } else {
  //   routes = protectedRoutes;
  // }

  if (pharma.isCRC || pharma.isPV) {
    if (pharma.isSiteLoggedIn) {
      routes = protectedRoutes;
    } else {
      routes = publicRoutes;
    }
  }

  // DFI18231 (ADDED; SIENNA branch above unchanged): acting roles (CRC / Study Team / VV) use the
  // same "act as site" selector as a SIENNA CRC — public selector before a site is chosen, protected
  // /app after. subUsers are populated by the seevividly.com backend (GetPharma1Sites, study-aware).
  if (isDfiCrc || isDfiStudyTeam || isDfiVv) {
    routes = pharma.isSiteLoggedIn ? protectedRoutes : publicRoutes;
  }

  // DFI18231 Device Admin (without VV) goes to /devices like a SIENNA PV — public route set.
  if (isDfiDeviceAdmin && !isDfiVv) {
    routes = publicRoutes;
  }

  if (isRawSiteStaff) {
    routes = [];
    commonRoutes.push({ path: '*', element: invalidSession });
  }

  const element = useRoutes([...routes, ...commonRoutes]);

  return <>{element}</>;
};
